import React, { useState } from 'react';
import { get, set, first } from 'global/lib/lodash';
import { Link, Icon } from '@material-ui/core';
import { Column, Row } from 'global/components/row';
import Form, { convertObjToJbuilder, convertJBuilderToObj } from 'global/components/form';
import { Text } from 'global/components/text';
import Modal from 'global/components/modal';
import useStyles from './styles';
import { Button } from 'global/components/Button';
import SnackAlerts from 'global/components/snack-alerts';
import colors from 'global/styles/colors';
import sendData from 'redux/actions/sendData';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { stringObj } from '../../../global/utils';
import { withRouter } from 'react-router-dom';

export const EdDocModal = withRouter((props) => {
  const { doc, isCourseType } = props;
  const classes = useStyles();
  const [alerts, setAlerts] = useState();
  const [formState, setFormState] = useState({});

  const submit = async () => {
    try {
      let superset = get(formState, 'superset');
      if (superset && typeof superset === 'string') {
        const result = first(
          await sendData(
            {
              url: '/supersets',
              method: 'post',
              data: {
                data: {
                  type: 'supersets',
                  attributes: {
                    name: superset,
                  },
                },
              },
            },
            {
              normalize: {
                build: ['supersets'],
              },
            }
          )
        );

        superset = result;
      }

      const formatData = () => {
        const obj = {
          id: doc.id,
          type: 'edition-documents',
        };

        const meta = convertJBuilderToObj(formState.meta);

        const getOr = (sel) => {
          return get(formState, sel, get(doc, sel));
        };

        set(obj, 'attributes', {
          order: getOr('order'),
          primary_document: getOr('primary_document.selected') ? 1 : 0,
          meta,
        });

        if (superset && superset.id) {
          set(obj, 'relationships', {
            superset: {
              data: stringObj({
                type: 'supersets',
                id: superset.id,
              }),
            },
          });
        }

        return {
          data: obj,
        };
      };

      await sendData({
        url: `/edition-documents/${doc.id}`,
        method: 'patch',
        data: formatData(),
      });
      onClose(true);
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    }
  };

  const onClose = (updated = false) => {
    props.onClose && props.onClose(updated);
  };

  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      waitFor: isCourseType,
      build: ['supersets'],
    }
  );

  const orderDisabled = !get(formState, 'primary_document.selected', false);

  const submitDisabled = (() => {
    if (!orderDisabled && !get(formState, 'order') && get(formState, 'order') !== 0) {
      return true;
    }
    return get(formState, 'meta', []).find((f) => !f.valid);
  })();

  return (
    <Modal {...props} onClose={onClose}>
      {!!props.open && (
        <Column center fullWidth>
          <Text type="label_medium">
            You are editing the relationship that controls how and where a document appears in the Library Edition. To edit the document itself, please do so through the Library{' '}
            <Link underline="always" onClick={() => props.history.push('/library')}>
              here.
            </Link>
          </Text>

          <div style={{ width: '100%', marginBottom: 34 }}>
            <Form
              onFormState={setFormState}
              showAllScreens
              screens={[
                {
                  inputs: [
                    !isCourseType && {
                      id: 'primary_document',
                      type: 'Checkbox',
                      defaultValue: () => ({ selected: !!get(props, 'doc.primaryDocument') }),
                      options: [{ value: 'selected', label: 'Primary Document' }],
                      props: {
                        label: 'Primary',
                        tooltip: {
                          title: `When checked, this setting indicates that this document is the default document in it's section. The title of this document will be the section header, and when a user clicks on it's section, this document will be the first tab they're directed to.`,
                        },
                      },
                    },
                    isCourseType && {
                      id: 'superset',
                      type: 'Autocomplete',
                      defaultValue: () => get(doc, 'superset'),
                      data: () => supersets.data,
                    },
                    {
                      id: 'order',
                      type: 'TextField',
                      defaultValue: () => get(props, 'doc.order'),
                      props: {
                        type: 'number',
                        variant: orderDisabled ? 'filled' : undefined,
                        disabled: orderDisabled,
                        label: 'Order',
                      },
                    },
                    {
                      id: 'meta',
                      type: 'JsonBuilder',
                      defaultValue: () => convertObjToJbuilder(get(props, 'doc.meta', {})),
                      options: [
                        {
                          id: 'duration',
                          label: 'Duration',
                          props: {
                            type: 'duration',
                          },
                        },
                        {
                          id: 'type',
                          label: 'Type',
                          props: {
                            type: 'Select',
                            options: [
                              { value: 'checklist', label: 'Checklist' },
                              { value: 'quiz', label: 'Quiz' },
                            ],
                          },
                        },
                        { id: 'random', label: 'Random Options', props: { type: 'bool' } },
                        { id: 'randomcheck', label: 'Random Checkpoints', props: { type: 'bool' } },
                        { id: 'questcount', label: 'Question Count', props: { type: 'number' } },
                        {
                          id: 'pass',
                          label: 'Passing Score',
                          props: {
                            type: 'number',
                            isValid: (v) => {
                              const val = parseInt(v);
                              return !isNaN(val) && val >= 0;
                            },
                          },
                        },
                      ],
                      props: {
                        label: 'Properties',
                        tooltip: {
                          title: `Properties help define how the document interacts with the site, particularly for courses.`,
                        },
                      },
                    },
                  ].filter((f) => !!f),
                },
              ]}
            />
          </div>

          <Row className={classes.buttonRowModal}>
            <Button startIcon={<Icon>close</Icon>} bgColor={colors.danger} onClick={() => onClose()}>
              CANCEL
            </Button>
            <Button disabled={!!submitDisabled} startIcon={<Icon>save</Icon>} onClick={() => submit()}>
              SAVE
            </Button>
          </Row>

          <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
        </Column>
      )}
    </Modal>
  );
});
